import { callAPI } from '../../../services/api.service';
import { HTTPStatusEnum } from '../../../interfaces/log.interface';
import { Toast } from '../../../components/Common/Toast';

export interface GoogleClassroomCourse {
  id: string;
  name: string;
  section?: string;
  description?: string;
  room?: string;
  ownerId: string;
  creationTime: string;
  updateTime: string;
  state: 'ACTIVE' | 'ARCHIVED' | 'PROVISIONED' | 'DECLINED';
  enrollmentCode?: string;
  courseGroupEmail?: string;
  teacherFolder?: {
    id: string;
    title: string;
    alternateLink: string;
  };
}

export interface GoogleClassroomStudent {
  id: string;
  userId: string;
  profile: {
    id: string;
    name: {
      givenName: string;
      familyName: string;
      fullName: string;
    };
    emailAddress: string;
    permissions: string[];
    photoUrl?: string;
  };
  courseId: string;
}

/**
 * Check if teacher has Google Classroom integration
 * @returns Boolean indicating if integration exists
 */
export const checkGoogleClassroomIntegration = async (): Promise<boolean> => {
  try {
    const response = await callAPI('api/google-classroom/courses', null, 'GET');
    return response?.status === HTTPStatusEnum.OK;
  } catch (error) {
    return false;
  }
};

/**
 * Get teacher's Google Classroom courses using server-managed token
 * @returns List of Google Classroom courses or null if failed
 */
export const getGoogleClassroomCourses = async (): Promise<GoogleClassroomCourse[] | null> => {
  try {
    const response = await callAPI('api/google-classroom/courses', null, 'GET');

    if (response?.status !== HTTPStatusEnum.OK) {
      Toast(response?.error || 'Failed to fetch Google Classroom courses', true);
      return null;
    }

    return response.courses || [];
  } catch (error) {
    Toast('Failed to fetch Google Classroom courses', true);
    return null;
  }
};

/**
 * Get students from a specific Google Classroom course using server-managed token
 * @param courseId Google Classroom course ID
 * @param classroomId Target classroom ID for filtering existing students
 * @returns List of Google Classroom students or null if failed
 */
export const getGoogleClassroomCourseStudents = async (
  courseId: string,
  classroomId: string
): Promise<GoogleClassroomStudent[] | null> => {
  try {
    const response = await callAPI(
      `api/google-classroom/courses/${courseId}/students?classroomId=${classroomId}`,
      null,
      'GET'
    );

    if (response?.status !== HTTPStatusEnum.OK) {
      Toast(response?.error || 'Failed to fetch students from Google Classroom', true);
      return null;
    }

    return response.students || [];
  } catch (error) {
    Toast('Failed to fetch students from Google Classroom', true);
    return null;
  }
};

/**
 * Import students from Google Classroom course to Build My Story classroom using server-managed token
 * @param courseId Google Classroom course ID
 * @param targetClassroomId Target Build My Story classroom ID
 * @param targetProjectId Target project ID
 * @param selectedStudentIds Array of selected student IDs
 * @param isOnboarding
 * @returns API response or null if failed
 */
export const importGoogleClassroomStudents = async (
  courseId: string,
  targetClassroomId: string,
  targetProjectId: string,
  selectedStudentIds: string[],
  isOnboarding?: boolean,
): Promise<any> => {
  try {
    const response = await callAPI(
      `api/google-classroom/courses/${courseId}/import`,
      {
        targetClassroomId,
        targetProjectId,
        selectedStudentIds,
        isOnboarding: !!isOnboarding,
      },
      'POST',
    );

    if (response?.status === HTTPStatusEnum.OK) {
      Toast(response?.message || 'Students successfully imported from Google Classroom', false);
    } else {
      Toast(response?.error || 'Failed to import students from Google Classroom', true);
    }

    return response;
  } catch (error) {
    Toast('Failed to import students from Google Classroom', true);
    return null;
  }
};

/**
 * Get Google Classroom course details
 * @param courseId Google Classroom course ID
 * @returns Course details or null if failed
 */
export const getGoogleClassroomCourseDetails = async (
  courseId: string
): Promise<GoogleClassroomCourse | null> => {
  try {
    const response = await callAPI(
      `api/google-classroom/courses/${courseId}`,
      null,
      'GET'
    );

    if (response?.status !== HTTPStatusEnum.OK) {
      Toast(response?.error || 'Failed to fetch course details', true);
      return null;
    }

    return response.course || null;
  } catch (error) {
    Toast('Failed to fetch course details', true);
    return null;
  }
};

/**
 * Revoke Google Classroom integration
 * @returns Boolean indicating success
 */
export const revokeGoogleClassroomIntegration = async (): Promise<boolean> => {
  try {
    const response = await callAPI('api/google-classroom/integration', null, 'DELETE');

    if (response?.status === HTTPStatusEnum.OK) {
      Toast('Google Classroom integration revoked successfully', false);
      return true;
    } else {
      Toast(response?.error || 'Failed to revoke integration', true);
      return false;
    }
  } catch (error) {
    Toast('Failed to revoke Google Classroom integration', true);
    return false;
  }
};
